import { tt } from '@agos/shared'

/**
 * Whether a section may draw its empty state - FB-807, `FRONTEND.md` §9.528.
 *
 * **An empty state is a statement about the customer**, and the account screen was making it on the
 * strength of a read that had failed: a query that errors arrives as `undefined`, its parser answers
 * `[]`, and the card says *you have no coupons* to somebody holding three - then invites them to go and
 * win some. Seven of the screen's ten reads did this; only `GET /customer-auth/me` was guarded.
 *
 * **§10A.245 is why this is not hypothetical.** It fixed `personalized-suggestion` answering 500 for any
 * customer with no e-mail, found in Nesvady's own `system_logs` - seventeen in one hour. And no server
 * fault is needed: `retry` is off on these queries and the customer app's rate limiter is keyed per
 * address (§10A.255), so a phone behind a carrier's NAT can take a 429.
 */

/**
 * The part of a query this rule needs.
 *
 * Declared rather than importing react-query's own type, because the rule is about two booleans and a
 * test should be able to hand it two booleans. Both optional, so a caller may pass a query straight in.
 */
export interface SectionRead {
  isError?: boolean
  isLoading?: boolean
}

/**
 * Whether this read failed in a way the customer should be told about.
 *
 * **`isError` alone, and the first draft of this function was wrong to add to it.** It also required
 * `fetchStatus !== 'idle'`, reasoning that a query which has never run must not be reported as broken -
 * and that is **the state react-query leaves a settled failure in**: `isError: true` with
 * `fetchStatus: 'idle'`, because nothing is fetching any more. So the extra condition suppressed exactly
 * the case this predicate exists for, and the fix would have shipped doing nothing.
 *
 * The reasoning behind it was still right and is why one flag is enough: **a query that has not run is
 * not an error.** With `enabled: false` - which two of these reads use while their tab is closed -
 * react-query reports it pending with `isError: false`, so it never reaches this branch. *Still loading*
 * is likewise not a failure, and an error superseded by a successful refetch clears `isError` itself, so
 * neither needs a special case.
 */
export function couldNotLoad(read: SectionRead | undefined): boolean {
  return read?.isError === true
}

/**
 * What the card says in place of its list.
 *
 * The existing *"...sa nepodarilo načítať"* shape, because the screen already says it for the account
 * read and a second wording for the same event would read as a different kind of problem.
 */
export function unreachableNote(): string {
  return tt('Túto časť sa nepodarilo načítať.')
}

/** The retry's own label, matching the account read's button so the two are one affordance. */
export function retryLabel(): string {
  return tt('Skúsiť znova')
}
